import * as React from "react";
import { createPortal } from "react-dom";

/** Partial overlay state passed from Tooltip via TooltipProvider.updateState. */
export type StateUpdate = {
    show?: boolean;
    content?: React.ReactNode;
    class_name?: string;
    x?: number;
    y?: number;
    offset_x?: number;
    offset_y?: number;
    style?: React.CSSProperties;
    interactive?: boolean;
    trigger?: TooltipTrigger;
};

type TargetDataset = {
    isTooltipActive?: string;
    title?: string;
};

/** How the tooltip is opened: pointer hover or explicit click. */
export type TooltipTrigger = "click" | "hover";

type Props = {};

type State = {
    show: boolean;
    enable: boolean;
    content: React.ReactNode;
    class_name: string;
    x: number;
    y: number;
    offset_x: number;
    offset_y: number;
    position_x: number;
    position_y: number;
    width: number;
    height: number;
    style: React.CSSProperties;
    is_focused: boolean;
    is_mouse_over: boolean;
    is_mouse_over_content: boolean;
    interactive: boolean;
    target: Element | null;
    trigger: TooltipTrigger;
    container: Element | null;
};

/**
 * Renders the shared tooltip in a portal and owns position, size, and visibility.
 * Driven by TooltipProvider; responds to tooltip-enable and tooltip-disable window events.
 */
class TooltipOverlay extends React.Component<Props, State> {
    tooltip_ref: HTMLElement | null = null;
    content_ref: HTMLElement | null = null;

    state: State = {
        show: false,
        enable: true,
        content: null,
        class_name: "",
        x: 0,
        y: 0,
        offset_x: 0,
        offset_y: 0,
        position_x: 0,
        position_y: 0,
        width: 0,
        height: 0,
        style: {},
        is_focused: false,
        is_mouse_over: false,
        is_mouse_over_content: false,
        interactive: false,
        target: null,
        trigger: "hover",
        container: document.body,
    };

    getShow = (): boolean => {
        return this.state.show;
    };

    isTarget = (ref: Element | null): boolean => {
        return ref !== null && this.state.target === ref;
    };

    componentDidMount() {
        window.addEventListener("tooltip-disable", this.disableTooltip);
        window.addEventListener("tooltip-enable", this.enableTooltip);
    }

    componentWillUnmount() {
        window.removeEventListener("tooltip-disable", this.disableTooltip);
        window.removeEventListener("tooltip-enable", this.enableTooltip);
    }

    disableTooltip = () => {
        this.setState({
            enable: false,
        });
    };

    enableTooltip = () => {
        this.setState({
            enable: true,
            show: false,
        });
    };

    updateState = (ref: Element | null, container: Element | null, state: StateUpdate) => {
        this.setState(
            prev_state => {
                const new_state = {
                    ...prev_state,
                    ...state,
                    target: ref,
                };

                if ((this.state.interactive && this.state.is_mouse_over) || this.state.is_focused) {
                    new_state.show = true;
                }

                if (!new_state.content) {
                    new_state.show = false;
                }

                if (
                    (!new_state.show && prev_state.target !== null) ||
                    (new_state.show && prev_state.target !== null && prev_state.target !== ref)
                ) {
                    this.setTargetAttributes(false, prev_state.target);
                }

                if (container == null) {
                    new_state.container = document.body;
                }

                return new_state;
            },
            () => {
                if (!this.optimizePosition(this.measureFullSizeTooltip())) {
                    this.optimizePosition(this.measureTooltip(), true);
                }

                this.updateTarget();
            }
        );
    };

    updateTarget = () => {
        const target = this.state.target;
        if (target === null) {
            return;
        }

        this.setTargetAttributes(this.state.show);
    };

    setTargetAttributes = (show: boolean, target: Element | null = null) => {
        const tooltip_target = (target || this.state.target) as HTMLElement | null;
        if (tooltip_target === null) {
            return;
        }

        const dataset: TargetDataset = tooltip_target.dataset;
        if (!dataset) {
            return;
        }

        if (show) {
            dataset.isTooltipActive = "true";
            if (tooltip_target.title) {
                dataset.title = tooltip_target.title;
                tooltip_target.removeAttribute("title");
            }
        } else {
            delete dataset.isTooltipActive;
            if (dataset.title) {
                tooltip_target.title = dataset.title;
                delete dataset.title;
            }
        }
    };

    getViewportWidth = (): number => {
        return document.body ? document.body.offsetWidth : window.innerWidth;
    };

    getViewportHeight = (): number => {
        return document.body ? document.body.offsetHeight : window.innerHeight;
    };

    getMaxWidth = (): number => {
        const viewport_width = this.getViewportWidth();

        if (this.content_ref === null) {
            return viewport_width;
        }

        const max_width = parseFloat(getComputedStyle(this.content_ref).maxWidth);
        if (Number.isNaN(max_width)) {
            return viewport_width;
        }

        return Math.min(max_width, viewport_width);
    };

    getMaxHeight = (): number => {
        const viewport_height = this.getViewportHeight();

        if (this.content_ref === null) {
            return viewport_height;
        }

        const max_height = parseFloat(getComputedStyle(this.content_ref).maxHeight);
        if (Number.isNaN(max_height)) {
            return viewport_height;
        }

        return Math.min(max_height, viewport_height);
    };

    getTooltipRect = (): DOMRect | null => {
        return this.content_ref !== null ? this.content_ref.getBoundingClientRect() : null;
    };

    updateMaxSize = () => {
        const target = this.content_ref;
        if (!target) {
            return;
        }

        target.style.maxWidth = `${this.getMaxWidth()}px`;
        target.style.maxHeight = `${this.getMaxHeight()}px`;
    };

    measureTooltip = (): DOMRect | null => {
        this.updateMaxSize();
        return this.getTooltipRect();
    };

    measureFullSizeTooltip = (): DOMRect | null => {
        const target = this.tooltip_ref;
        if (!target) {
            return null;
        }

        const { top, left } = target.style;
        target.style.top = "0";
        target.style.left = "0";
        this.updateMaxSize();
        const rect = this.getTooltipRect();
        target.style.top = top;
        target.style.left = left;

        return rect;
    };

    optimizePosition = (rect: DOMRect | null, force: boolean = false): boolean => {
        if (!rect) {
            return false;
        }

        const { x, y, offset_x, offset_y } = this.state;
        const { width, height } = rect;
        const viewport_width = this.getViewportWidth();
        const viewport_height = this.getViewportHeight();
        const diff_x = viewport_width - (x + offset_x + width);
        const diff_y = viewport_height - (y + offset_y + height);
        const opt_x = Math.max(0, viewport_width - width);
        const opt_y = Math.max(0, viewport_height - height);
        let position_x = x + offset_x;
        let position_y = y + offset_y;

        if (diff_x < 0 && diff_y >= 0) {
            position_x = opt_x;
        } else if (diff_y < 0 && diff_x >= 0) {
            position_y = opt_y;
        } else if (diff_x < 0 && diff_y < 0) {
            const min_x = x - width - offset_x + 1;
            const min_y = y - height - offset_y + 1;

            if (min_x < 0 && min_y < 0) {
                if (!force) {
                    return false;
                }

                position_y = opt_y;
            } else {
                position_x = opt_x;
                position_y = opt_y;

                if (min_x >= 0) {
                    position_x = min_x;
                }

                if (min_y >= 0) {
                    position_y = min_y;
                }
            }
        }

        this.setState(
            {
                position_x,
                position_y,
                width,
                height,
            },
            this.updateTarget
        );

        return true;
    };

    getTooltipStyle = (): React.CSSProperties => {
        const { x, y, position_x, position_y, width, height, is_mouse_over_content, interactive, trigger } = this.state;
        const style = {
            top: position_y,
            left: position_x,
            paddingTop: 0,
            paddingRight: 0,
            paddingBottom: 0,
            paddingLeft: 0,
        };

        if (interactive && trigger === "hover" && !is_mouse_over_content) {
            const offset = 2;
            if (position_x > x) {
                style.paddingLeft = position_x - x - offset;
                style.left = x + offset;
            } else if (position_x < x - width) {
                style.paddingRight = x - (position_x + width) - offset + 1;
            }

            if (position_y > y) {
                style.paddingTop = position_y - y - offset;
                style.top = y + offset;
            } else if (position_y < y - height) {
                style.paddingBottom = y - (position_y + height) - offset + 1;
            }
        }

        return {
            ...this.state.style,
            position: "fixed",
            ...style,
        };
    };

    handleMouseOver = () => {
        if (!this.state.interactive || this.state.trigger !== "hover") {
            return;
        }

        this.setState(
            {
                is_mouse_over: true,
                show: true,
            },
            this.updateTarget
        );
    };

    handleMouseOut = () => {
        if (!this.state.interactive || this.state.trigger !== "hover") {
            return;
        }

        this.setState(
            {
                is_mouse_over: false,
                show: false,
            },
            this.updateTarget
        );
    };

    handleFocus = () => {
        this.setState(
            {
                is_focused: true,
                show: true,
            },
            this.updateTarget
        );
    };

    handleBlur = () => {
        this.setState(
            {
                is_focused: false,
                show: false,
            },
            this.updateTarget
        );
    };

    handleContentMouseOver = () => {
        if (!this.state.interactive) {
            return;
        }

        this.setState(
            {
                is_mouse_over_content: true,
            },
            this.updateTarget
        );
    };

    handleContentMouseOut = () => {
        this.setState(
            {
                is_mouse_over_content: false,
            },
            this.updateTarget
        );
    };

    render() {
        const container = this.state.container;
        if (container === null) {
            return null;
        }

        if (!this.state.show || !this.state.enable) {
            return null;
        }

        return createPortal(
            <div
                role="tooltip"
                id="custom-tooltip"
                ref={ref => {
                    this.tooltip_ref = ref;
                }}
                className={`custom-tooltip ${this.state.class_name}`}
                style={this.getTooltipStyle()}
                onFocus={this.handleFocus}
                onBlur={this.handleBlur}
                onMouseEnter={this.handleMouseOver}
                onMouseLeave={this.handleMouseOut}
            >
                <div
                    className="custom-tooltip-content"
                    ref={ref => {
                        this.content_ref = ref;
                    }}
                    tabIndex={this.state.trigger === "click" ? 0 : undefined}
                    onMouseEnter={this.handleContentMouseOver}
                    onMouseLeave={this.handleContentMouseOut}
                >
                    {this.state.content}
                </div>
            </div>,
            container
        );
    }
}

export default TooltipOverlay;
